import { createContext, useCallback, useMemo, useState } from 'react';
import type { JSX } from 'react';

import { PaletteModeEnum } from '@e-socna/data-layer';
import { NULL_FUNCTION } from '@e-socna/empty';

import { resolveThemeMainColor } from './utils/resolve-theme-main-color.util';
import type { SpaceLevel } from './enums/space-level.enum';
import type {
  SpaceEmitterColors,
  ThemeColorSettings,
} from './utils/resolve-theme-main-color.util';

export interface UiSettings extends ThemeColorSettings {
  readonly paletteMode: PaletteModeEnum;
}

interface UiSettingsData {
  readonly paletteMode: PaletteModeEnum;
  readonly mainColor: string;
  readonly setUiSettings: (uiSettings: UiSettings) => void;
  readonly setSpaceEmitterColor: (
    level: SpaceLevel,
    color: string | undefined,
  ) => void;
}

const DEFAULT_UI_SETTINGS: UiSettings = {
  paletteMode: PaletteModeEnum.Light,
  themeMainColor: null,
  isContextualColorEnabled: true,
};

const UiSettingsContext = createContext<UiSettingsData>({
  paletteMode: DEFAULT_UI_SETTINGS.paletteMode,
  mainColor: resolveThemeMainColor(DEFAULT_UI_SETTINGS, {}),
  setUiSettings: NULL_FUNCTION,
  setSpaceEmitterColor: NULL_FUNCTION,
});

interface Props {
  readonly children: JSX.Element | readonly JSX.Element[];
}

// The UI settings of the authenticated user, and the quotation emitter colour of the business
// entity space shown, which the theme takes when the user lets it.
const UiSettingsContextProvider = ({ children }: Readonly<Props>) => {
  const [uiSettings, setUiSettings] = useState(DEFAULT_UI_SETTINGS);
  const [spaceEmitterColors, setSpaceEmitterColors] =
    useState<SpaceEmitterColors>({});

  const setSpaceEmitterColor = useCallback(
    (level: SpaceLevel, color: string | undefined) => {
      setSpaceEmitterColors((previousColors) => ({
        ...previousColors,
        [level]: color,
      }));
    },
    [],
  );

  const value = useMemo(
    () => ({
      paletteMode: uiSettings.paletteMode,
      mainColor: resolveThemeMainColor(uiSettings, spaceEmitterColors),
      setUiSettings,
      setSpaceEmitterColor,
    }),
    [uiSettings, spaceEmitterColors, setSpaceEmitterColor],
  );

  return (
    <UiSettingsContext.Provider value={value}>
      {children}
    </UiSettingsContext.Provider>
  );
};

export { UiSettingsContext, UiSettingsContextProvider };
